import {mxClient, mxEvent, mxResources, mxUtils} from './mxGraphFactory';

const kebabCase = str => str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    .map(x => x.toLowerCase())
    .join('-');

/**
 * Constructs a new toolbar for the given editor.
 */
export const Toolbar = function (editorUi, container) {
    this.editorUi = editorUi;
    this.container = container;
    this.init();

    // Global handler to hide the current menu
    this.gestureHandler = mxUtils.bind(this, function (evt) {
        if (this.editorUi.currentMenu != null && mxEvent.getSource(evt) !== this.editorUi.currentMenu.div) {
            this.hideMenu();
        }
    });

    mxEvent.addGestureListeners(document, this.gestureHandler);
};

/**
 * Adds the toolbar elements.
 */
Toolbar.prototype.init = function () {
    let group = document.createElement('div');
    group.className = 'ui small basic buttons';
    let elts = this.addItems(['zoomIn', 'zoomOut', 'resetView'], group);
    elts[0].setAttribute('title', mxResources.get('zoomIn') + ' (' + this.editorUi.actions.get('zoomIn').shortcut + ')');
    elts[1].setAttribute('title', mxResources.get('zoomOut') + ' (' + this.editorUi.actions.get('zoomOut').shortcut + ')');
    elts[2].setAttribute('title', mxResources.get('resetView') + ' (' + this.editorUi.actions.get('resetView').shortcut + ')');
    this.container.appendChild(group);

    if (this.editorUi.editor.editable) {
        this.container.appendChild(document.createTextNode(' '));

        group = document.createElement('div');
        group.className = 'ui small basic buttons';
        elts = this.addItems(['undo', 'redo'], group);
        elts[0].setAttribute('title', mxResources.get('undo') + ' (' + this.editorUi.actions.get('undo').shortcut + ')');
        elts[1].setAttribute('title', mxResources.get('redo') + ' (' + this.editorUi.actions.get('redo').shortcut + ')');
        this.container.appendChild(group);

        this.container.appendChild(document.createTextNode(' '));

        group = document.createElement('div');
        group.className = 'ui small basic buttons';
        elts = this.addItems(['delete'], group);
        elts[0].setAttribute('title', mxResources.get('delete') + ' (' + this.editorUi.actions.get('delete').shortcut + ')');
        this.container.appendChild(group);

        // this.container.appendChild(document.createTextNode(' '));
        //
        // group = document.createElement('div');
        // group.className = 'ui small basic buttons';
        // elts = this.addItems(['save'], group);
        // elts[0].setAttribute('title', mxResources.get('save') + ' (' + this.editorUi.actions.get('save').shortcut + ')');
        // this.container.appendChild(group);
    }
};

/**
 * Hides the current menu.
 */
Toolbar.prototype.hideMenu = function () {
    this.editorUi.hideCurrentMenu();
};

/**
 * Adds a label to the toolbar.
 */
Toolbar.prototype.addMenuFunctionInContainer = function (container, label, tooltip, showLabels, funct, showAll) {
    const elt = (showLabels) ? this.createLabel(label) : this.createButton(label);
    this.initElement(elt, tooltip);
    this.addMenuHandler(elt, showLabels, funct, showAll);
    container.appendChild(elt);
    return elt;
};

/**
 * Adds a separator to the separator.
 */
Toolbar.prototype.addSeparator = function (c) {
    c = (c != null) ? c : this.container;
    const elt = document.createElement('div');
    elt.className = 'geSeparator';
    c.appendChild(elt);

    return elt;
};

/**
 * Adds given action item
 */
Toolbar.prototype.addItems = function (keys, c, ignoreDisabled) {
    const items = [];

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];

        if (key === '-') {
            items.push(this.addSeparator(c));
        } else {
            items.push(this.addItem(kebabCase(key), key, c, ignoreDisabled));
        }
    }

    return items;
};

/**
 * Adds given action item
 */
Toolbar.prototype.addItem = function (sprite, key, c, ignoreDisabled) {
    const action = this.editorUi.actions.get(key);
    let elt = null;

    if (action != null) {
        let tooltip = action.label;

        if (action.shortcut != null) {
            tooltip += ' (' + action.shortcut + ')';
        }

        elt = this.addButton(sprite, tooltip, action.funct, c);

        if (!ignoreDisabled) {
            elt.setEnabled(action.enabled);

            action.addListener('stateChanged', function () {
                elt.setEnabled(action.enabled);
            });
        }
    }

    return elt;
};

/**
 * Adds a button to the toolbar.
 */
Toolbar.prototype.addButton = function (className, tooltip, funct, c) {
    const elt = this.createButton(className);
    c = (c != null) ? c : this.container;

    this.initElement(elt, tooltip);
    this.addClickHandler(elt, funct);
    c.appendChild(elt);

    return elt;
};

/**
 * Initializes the given toolbar element.
 */
Toolbar.prototype.initElement = function (elt, tooltip) {
    // Adds tooltip
    if (tooltip != null) {
        elt.setAttribute('title', tooltip);
    }

    this.addEnabledState(elt);
};

/**
 * Adds enabled state with setter to DOM node (avoids JS wrapper).
 */
Toolbar.prototype.addEnabledState = function (elt) {
    const classname = elt.className;

    elt.setEnabled = function (value) {
        elt.enabled = value;

        if (value) {
            elt.className = classname;
        } else {
            elt.className = classname + ' disabled';
        }
    };

    elt.setEnabled(true);
};

/**
 * Adds enabled state with setter to DOM node (avoids JS wrapper).
 */
Toolbar.prototype.addClickHandler = function (elt, funct) {
    if (funct != null) {
        mxEvent.addListener(elt, 'click', function (evt) {
            if (elt.enabled) {
                funct(evt);
            }

            mxEvent.consume(evt);
        });

        if (document.documentMode != null && document.documentMode >= 9) {
            // Prevents focus
            mxEvent.addListener(elt, 'mousedown', function (evt) {
                evt.preventDefault();
            });
        }
    }
};

/**
 * Creates and returns a new button.
 */
Toolbar.prototype.createButton = function (className) {
    const elt = document.createElement('button');
    elt.className = 'ui icon button';
    elt.role = 'button';

    const inner = document.createElement('i');
    inner.setAttribute('aria-hidden', 'true');

    if (className === 'reset-view') {
        inner.className = 'search icon';
    } else if (className === 'zoom-in') {
        inner.className = 'search plus icon';
    } else if (className === 'zoom-out') {
        inner.className = 'search minus icon';
    } else if (className === 'delete') {
        inner.className = 'trash icon';
    } else if (className === 'launch') {
        inner.className = 'play icon';
    } else {
        inner.className = className + ' icon';
    }

    elt.appendChild(inner);

    return elt;
};


/**
 * Creates and returns a new button.
 */
Toolbar.prototype.createLabel = function (label) {
    const elt = document.createElement('a');
    elt.className = 'geLabel';
    mxUtils.write(elt, label);
    return elt;
};

/**
 * Adds a handler for showing a menu in the given element.
 */
Toolbar.prototype.addMenuHandler = function (elt, showLabels, funct, showAll) {
    if (funct != null) {
        const graph = this.editorUi.editor.graph;
        let menu = null;
        let show = true;

        mxEvent.addListener(elt, 'click', mxUtils.bind(this, function (evt) {
            if (show && (elt.enabled == null || elt.enabled)) {
                graph.popupMenuHandler.hideMenu();
                menu = new mxPopupMenu(funct);
                menu.div.className += ' geToolbarMenu';
                menu.showDisabled = showAll;
                menu.labels = showLabels;
                menu.autoExpand = true;

                var offset = mxUtils.getOffset(elt);
                menu.popup(offset.x, offset.y + elt.offsetHeight, null, evt);
                this.editorUi.setCurrentMenu(menu, elt);

                // Workaround for scrollbar hiding menu items
                if (!showLabels && menu.div.scrollHeight > menu.div.clientHeight) {
                    menu.div.style.width = '40px';
                }

                menu.hideMenu = mxUtils.bind(this, function () {
                    mxPopupMenu.prototype.hideMenu.apply(menu, arguments);
                    this.editorUi.resetCurrentMenu();
                    menu.destroy();
                });

                // Extends destroy to reset global state
                menu.addListener(mxEvent.EVENT_HIDE, mxUtils.bind(this, function () {
                    this.currentElt = null;
                }));
            }

            show = true;
            mxEvent.consume(evt);
        }));

        // Hides menu if already showing and prevents focus
        mxEvent.addListener(elt, (mxClient.IS_POINTER) ? 'pointerdown' : 'mousedown',
            mxUtils.bind(this, function (evt) {
                show = this.currentElt !== elt;
                evt.preventDefault();
            }));
    }
};

/**
 * Adds a handler for showing a menu in the given element.
 */
Toolbar.prototype.destroy = function () {
    if (this.gestureHandler != null) {
        mxEvent.removeGestureListeners(document, this.gestureHandler);
        this.gestureHandler = null;
    }
};
